import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomFieldType, RenderSeparator, renderSeparatorString } from '@ee/common/enums';
import { ANY_SNAKE_CASE, CUSTOM_FIELD_CODE } from '@ee/common/constants';
import { AvailableFieldValidator, MULTI_VALUE_DELIMITER, ValidateQuillEquation } from '@ee/common/validators';
import {
  CustomField,
  CustomFieldOption,
  DATE_DEFAULT_BLANK,
  DATE_DEFAULT_TODAY
} from '@ee/common/models';
import { CUSTOM_FIELDS } from './custom-fields.constants';

export type CustomFieldForm = FormGroup<{
  order: FormControl<number>;
  columns: FormControl<number>;
  label: FormControl<string>;
  hint: FormControl<string>;
  hint_more_info: FormControl<string>;
  field: FormControl<string>;
  type: FormControl<CustomFieldType>;
  required: FormControl<boolean>;
  prevent_removal: FormControl<boolean>;
  link_id: FormControl<string>;
  false_label: FormControl<string>;
  true_label: FormControl<string>;
  include_in_total: FormControl<boolean>;
  monetary: FormControl<boolean>;
  hide: FormControl<boolean>;
  hide_from_intake: FormControl<boolean>;
  value: FormControl<any>;
  options: FormControl<string[]>;
  /**
   * MULTI_SELECT and RADIO only — the {label, value} pairs behind `options`.
   *
   * Two shapes, one meaning. The **builder** form edits them, so it holds a `FormArray` of editable
   * rows; the **render** form only reads them to caption a choice, so it holds the resolved list in a
   * single control. `.value` is `{label, value}[]` either way, so every reader is identical — only an
   * editor needs to know which it has.
   */
  option_definitions?: FormArray<CustomFieldOptionForm> | FormControl<CustomFieldOption[]>;
  /** MULTI_SELECT only — how selections join in documents/reports. Null for every other type. */
  render_separator?: FormControl<RenderSeparator | null>;
  /**
   * The value a NEW record's control starts at. Absent ⇒ no default. Present on both forms: the
   * builder edits it, and the render form carries it so the step-completion renderer can see it.
   */
  default_value?: FormControl<any>;
  /** TEXT only — a raw ngx-mask pattern, or null for an unrestricted input. */
  mask?: FormControl<string | null>;
  built_in_field: FormControl<boolean>;
  description: FormControl<string>;
  example: FormControl<string>;
}>;
export type CustomFieldOptionForm = FormGroup<{
  label: FormControl<string>;
  value: FormControl<string>;
}>;

/** One editable {label, value} option row of a MULTI_SELECT field. */
export function GenerateCustomFieldOptionForm(fb: FormBuilder, option?: CustomFieldOption): CustomFieldOptionForm {
  return fb.group({
    label: [option?.label ?? ''],
    value: [option?.value ?? '']
  });
}

/**
 * The option rows a MULTI_SELECT field opens with: its definitions, or — for a field that only ever
 * had bare `options` (a legacy SurveyJS checkbox question) — one row per option where the label
 * equals the value (Req 2.2).
 */
function choiceSeedOptions(values: CustomField | undefined): CustomFieldOption[] {
  return values?.option_definitions?.length
    ? values.option_definitions
    : (values?.options ?? []).map(value => ({value, label: value}));
}

/**
 * Fills every blank stored value in from its label (Req 4.6), across a MULTI_SELECT field's option
 * rows, and keeps `options` equal to the ordered list of the resulting values (Req 2.3).
 *
 * Called on load and again on save rather than only from the editor's blur handler: a row that is
 * never blurred — a keyboard submit, or definitions that arrive with a blank value — would otherwise
 * submit `{label: 'Mailed', value: ''}` while `options` already said `['Mailed']`, an inconsistent
 * payload the server's non-blank check rejects.
 */
export function NormalizeMultiSelectOptionValues(form: CustomFieldForm): void {
  const rows = form?.get('option_definitions') as FormArray;
  if (!rows) {
    return;
  }
  rows.controls.forEach(row => {
    const label = (row.get('label')?.value ?? '').trim();
    const value = (row.get('value')?.value ?? '').trim();
    if (!value && label) {
      row.get('value').setValue(label, {emitEvent: false});
    }
  });
  const values = rows.controls
    .map(row => ((row.get('value')?.value ?? '').trim() || (row.get('label')?.value ?? '').trim()))
    .filter(v => v);
  form.get('options')?.setValue(values, {emitEvent: false});
  rows.updateValueAndValidity({emitEvent: false});
}

/**
 * A MULTI_SELECT control always holds a real array while the user is ticking boxes, but the wire
 * shape is a single string: `CustomFieldDTO.value` is a `String?`, and the server splits it back
 * into a list with `CustomFieldUtils.splitMultiValueCell`. Req 2.5 forbids the delimiter inside any
 * option label or value (enforced by `MultiSelectOptionsValidator`), so the join is lossless.
 */
export function ParseMultiSelectValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    // Trimmed on both branches, or the same value round-trips differently depending on which side it
    // arrived from: ' A ' from the wire becomes 'A', while [' A '] from a control would stay ' A '.
    return value.map(v => `${v ?? ''}`.trim()).filter(v => !!v);
  }
  if (typeof value === 'string') {
    // '' is a real answer — "I ticked nothing" — and must parse to [], not ['']
    return value.split(MULTI_VALUE_DELIMITER).map(v => v.trim()).filter(v => !!v);
  }
  return [];
}

/**
 * The wire form of a MULTI_SELECT control's value. Nothing ticked yields `''`, never null: the
 * server drops fields whose value is null (`customFields.filter { it.value != null }`), so a null
 * would silently discard an answer the user actually gave.
 */
export function SerializeMultiSelectValue(value: unknown): string {
  return ParseMultiSelectValue(value).join(MULTI_VALUE_DELIMITER);
}

/**
 * One stored answer as it should READ on screen.
 *
 * A MULTI_SELECT value can reach a display surface still `;`-joined — most importantly a client
 * question's answer, which `answerClientQuestion` writes into `FieldValue.value`, a `String?` by
 * schema, so unlike a case custom field it is never converted to an array server-side. Printed raw
 * it leaks the wire delimiter to the user and ignores the separator the field is configured with
 * (Req 10.5).
 *
 * Shared, because there is more than one such surface — the action-centre step view and the case
 * dates/detail list — and a fix applied to only one of them is how this feature has repeatedly
 * shipped half-done. Every other field type is already a display string and passes straight through,
 * so this is safe to call unconditionally; a field that cannot be resolved is left alone rather than
 * guessed at, which keeps a TEXT answer that happens to contain ";" intact.
 */
export function FormatCustomFieldAnswerForDisplay(
  value: string,
  field: { type?: CustomFieldType; render_separator?: RenderSeparator | null } | null | undefined
): string {
  if (field?.type !== CustomFieldType.MULTI_SELECT) {
    return value;
  }
  return ParseMultiSelectValue(value).join(renderSeparatorString(field.render_separator));
}

/**
 * Rewrites every MULTI_SELECT entry of a submitted custom-field list into its wire form, leaving
 * every other type untouched. Call this on the payload object (a fresh `form.value`), never on the
 * form itself — the control must keep holding an array so the checkbox group stays bound to it.
 */
export function SerializeCustomFieldValuesForWire<T extends { type?: CustomFieldType; value?: any }>(
  fields: T[] | undefined | null
): T[] {
  return (fields ?? []).map(field =>
    field?.type === CustomFieldType.MULTI_SELECT
      ? { ...field, value: SerializeMultiSelectValue(field.value) }
      : field
  );
}

/**
 * Makes an intake form's sections safe to POST: any field placement whose `value` holds an array is
 * rewritten to the `;`-joined wire string (see {@link SerializeMultiSelectValue}).
 *
 * A placement is a `CustomFieldDTO`, whose `value` is a `String?` with no single-value-array
 * unwrapping, so one array anywhere in the payload fails the WHOLE save with a JSON parse error. The
 * builder preview no longer writes into a placement's `value`, but a form already dirtied in an open
 * tab (or any future writer) must still save. Scalars — including a CALCULATED field's equation,
 * which lives in `value` — pass through untouched. Returns new objects; the input is not mutated.
 */
export function SerializeIntakeFormSectionsForWire<T>(sections: T[]): T[] {
  const fix = (field: any) =>
    field && Array.isArray(field.value) ? {...field, value: SerializeMultiSelectValue(field.value)} : field;
  return (sections ?? []).map((section: any) => {
    if (!section) {
      return section;
    }
    return {
      ...section,
      ...(Array.isArray(section.fields) ? {fields: section.fields.map(fix)} : {}),
      ...(Array.isArray(section.built_in_fields) ? {built_in_fields: section.built_in_fields.map(fix)} : {}),
      ...(section.calculated_field ? {calculated_field: fix(section.calculated_field)} : {})
    };
  });
}

/**
 * The field types that may carry a `default_value`. The rest — the computed types, and the ones whose
 * value is a file, a row collection or a rent history — have no scalar value to default (Req 5.8).
 */
export const DEFAULTABLE_TYPES: ReadonlySet<CustomFieldType | undefined> = new Set([
  CustomFieldType.TEXT,
  CustomFieldType.MULTI_TEXT,
  CustomFieldType.DECIMAL,
  CustomFieldType.INTEGER,
  CustomFieldType.BOOLEAN,
  CustomFieldType.SELECT,
  CustomFieldType.RADIO,
  CustomFieldType.MULTI_SELECT,
  CustomFieldType.DATE
]);

/** Choice types whose `required` must be dropped when the field has no options to choose from. */
const CHOICE_TYPES_REQUIRING_OPTIONS: ReadonlySet<CustomFieldType> = new Set([
  CustomFieldType.SELECT,
  CustomFieldType.RADIO,
  CustomFieldType.MULTI_SELECT
]);

export interface DateDefaultSettings {
  default_optional_dates_to_today: boolean;
  default_required_dates_to_today: boolean;
}

/**
 * The value a control starts at when a form for a **new** record is built.
 *
 * The single place the per-type default semantics live, so the seven call sites that construct
 * custom-field controls cannot disagree: `GenerateCustomFieldForm` (the workflow step-completion path)
 * and the six `custom_fields` groups in `eviction.form.ts` (the attorney intake form). Six of those
 * seven are near-identical blocks that all hardcoded `undefined`, which is why a defaults change that
 * only touched `GenerateCustomFieldForm` would pass its own tests and do nothing on intake.
 *
 * **Only ever called where a control is created, never on a load or patch path.** A default applied when
 * a saved record is reopened would overwrite a blank the user deliberately left blank — and because
 * rendering dirties nothing, it would do so invisibly until the next unrelated save.
 *
 * A DATE field is the one type with four states rather than two:
 *
 * | `default_value`      | Behaviour                                                        |
 * |----------------------|------------------------------------------------------------------|
 * | absent               | **null here** — the caller decides whether to apply an org default |
 * | `DATE_DEFAULT_BLANK` | always empty, org setting ignored, required still enforced       |
 * | `DATE_DEFAULT_TODAY` | today, resolved here at construction so it cannot go stale       |
 * | an ISO date          | that fixed date                                                  |
 *
 * **The org [DateDefaultSettings] fallback is deliberately NOT applied here.** It belongs to the
 * workflow step-completion path only (see `.kiro/specs/date-field-defaults/`), and
 * `GenerateCustomFieldForm` applies it. Folding it in here would have made every DATE custom field on
 * the attorney intake form start prefilled with today — those six call sites pass no settings, and a
 * `?? true` fallback would then read as "yes, default to today" for a surface that has always opened
 * blank.
 */
export function defaultValueFor(customField: CustomField): any {
  const authored = customField?.default_value;

  if (customField?.type === CustomFieldType.DATE) {
    return dateDefaultFor(authored);
  }

  // Absent means "no default". An empty string is treated the same, so a cleared editor control does
  // not start pre-filling every new record with ''.
  if (authored === null || authored === undefined || authored === '') {
    // A checkbox group's value is always an array, never null — matching what the renderer writes when
    // every box is unticked.
    return customField?.type === CustomFieldType.MULTI_SELECT ? [] : null;
  }

  switch (customField.type) {
    case CustomFieldType.BOOLEAN:
      return authored === true || authored === 'true';
    case CustomFieldType.MULTI_SELECT:
      return Array.isArray(authored) ? [...authored] : [];
    default:
      return authored;
  }
}

/**
 * The DATE branch of [defaultValueFor]; see its table. Returns null for an absent default — the org
 * fallback is the caller's business, not this function's.
 */
function dateDefaultFor(authored: any): Date | null {
  if (authored === DATE_DEFAULT_BLANK || authored === null || authored === undefined || authored === '') {
    return null;
  }
  if (authored === DATE_DEFAULT_TODAY) {
    return new Date();
  }
  if (authored instanceof Date) {
    return authored;
  }
  if (typeof authored === 'string') {
    return parseLocalDate(authored);
  }
  return null;
}

/**
 * Parses a stored `yyyy-MM-dd` default as a **local** date.
 *
 * `new Date('2026-09-07')` is specified to parse a date-only string as UTC, which in every US timezone
 * is the previous day once the datepicker renders it locally — a default of the 7th showing as the 6th.
 * Anything that is not a bare ISO date falls through to `Date`'s own parsing, and an unparseable value
 * yields null rather than an Invalid Date in the control.
 */
function parseLocalDate(value: string): Date | null {
  const isoDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (isoDate) {
    const [, year, month, day] = isoDate;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}

export function GenerateCustomFieldForm(
  fb: FormBuilder,
  customField: CustomField = {} as CustomField,
  dateDefaults?: DateDefaultSettings
): CustomFieldForm {
  let required = customField.required;
  if (CHOICE_TYPES_REQUIRING_OPTIONS.has(customField.type)) {
    // A choice field with no options cannot be answered, so requiring it would deadlock the form:
    // nothing is selectable and the control can never become valid. RADIO belongs here for exactly
    // the same reason SELECT and MULTI_SELECT do.
    //
    // Gated on the choices the renderer will actually DRAW, not on `options` alone. Both the checkbox
    // group and the radio group seed from choiceSeedOptions, which PREFERS `option_definitions` — so a
    // field carrying definitions but no `options` draws a perfectly answerable control while silently
    // losing Validators.required, letting a required group submit empty (Req 5.3). For SELECT, which
    // never carries definitions, this is exactly the old `options.length > 0` test.
    required = required && choiceSeedOptions(customField).length > 0;
  }
  // BOOLEAN keeps starting at false rather than null: the toggle has no third state to render.
  const authoredDefault = customField.type === CustomFieldType.BOOLEAN
    ? (defaultValueFor(customField) ?? false)
    : defaultValueFor(customField);

  let value: any;
  if (customField.type === CustomFieldType.MULTI_SELECT) {
    // A MULTI_SELECT control holds an array from the very first render, so an untouched field
    // submits [] -> '' rather than undefined. A STORED answer arrives as the ';'-joined wire string
    // (CustomFieldDTO.value is a String), so it is parsed rather than assigned — and it beats the
    // authored default, which only ever seeds a record that has no answer yet.
    const stored = ParseMultiSelectValue(customField.value);
    value = stored.length ? stored : authoredDefault;
  } else if (customField.type === CustomFieldType.DATE && !customField.default_value) {
    // The org date-default settings apply on THIS path only (the workflow step-completion "Update
    // Status" tab), and only where the field authors no default of its own. A per-field default —
    // including the explicit BLANK sentinel — wins.
    value = dateDefaultToToday(customField, dateDefaults) ? new Date() : null;
  } else {
    value = authoredDefault;
  }

  // Req 11.4 — the step-completion renderer (`ee-custom-field`) reads the field off this group, so
  // without these it could only caption a choice from raw stored values, and could not mask.
  //
  // Added CONDITIONALLY, by type, for exactly the reason `GenerateCustomFieldBuilderForm` does it: four
  // workflow-editor call sites build this form for a field DEFINITION and persist `getRawValue()` onto
  // the step. An unconditional control therefore writes `option_definitions: []` / `default_value: null`
  // / `mask: null` onto every field of every type — and because step completion renders from that stored
  // copy, a pinned `default_value: null` means a default authored later in Field Settings never reaches
  // the step at all.
  //
  // A caller that wants these controls must pass the field's `type`; a form built with no type gets the
  // bare group, which is what a render form for an unknown field should be.
  const renderControls = {
    // MULTI_SELECT keeps the FormArray + separator the shipped checkbox renderer already reads: its
    // options are seeded through choiceSeedOptions, which prefers `option_definitions` over the
    // bare `options` list.
    ...(customField.type === CustomFieldType.MULTI_SELECT
      ? {
        option_definitions: fb.array(
          choiceSeedOptions(customField).map(option => GenerateCustomFieldOptionForm(fb, option))
        ),
        render_separator: [customField.render_separator ?? RenderSeparator.COMMA]
      }
      : {}),
    // RADIO only READS its definitions, to caption a choice, so the resolved list in a single control
    // is enough — `.value` is `{label, value}[]` either way, which is all `choiceOptions` wants.
    ...(customField.type === CustomFieldType.RADIO
      ? {option_definitions: [customField.option_definitions ?? []]}
      : {}),
    ...(DEFAULTABLE_TYPES.has(customField.type) ? {default_value: [customField.default_value ?? null]} : {}),
    ...(customField.type === CustomFieldType.TEXT ? {mask: [customField.mask ?? null]} : {})
  };

  // MULTI_SELECT only, and ADDED rather than left empty, for the same reason as the builder form
  // below: these keys must not appear on a field of any other type (Req 1.4). The render form needs
  // them because `ee-custom-field` captions its checkboxes from the definitions — without them it
  // could only show raw stored values.
  // Cast for the same reason GenerateCustomFieldBuilderForm casts: the spread of the conditional
  // MULTI_SELECT controls makes the inferred group a union, which no longer matches CustomFieldForm's
  // optional FormArray member.
  return fb.group({
    order: customField.order,
    columns: customField.columns,
    label: customField.label,
    hint: customField.hint,
    hint_more_info: customField.hint_more_info,
    field: customField.field,
    type: customField.type,
    required: required,
    link_id: customField.link_id,
    false_label: customField.false_label,
    true_label: customField.true_label,
    include_in_total: customField.include_in_total,
    monetary: customField.monetary ?? (customField.type === CustomFieldType.DECIMAL),
    hide: customField.hide,
    hide_from_intake: customField.hide_from_intake,
    built_in_field: customField.built_in_field || CUSTOM_FIELDS.some((f) => f.field === customField.field),
    value: [value, required && customField.type !== CustomFieldType.BOOLEAN ? [Validators.required] : []],
    options: [customField.options],
    ...renderControls,
    prevent_removal: customField.prevent_removal ?? false,
    description: customField.description,
    example: customField.example
    // Cast for the same reason GenerateCustomFieldBuilderForm casts: `option_definitions` is a
    // FormArray on the builder form and a single control here, and TypeScript will not narrow a union
    // property on assignment. Both shapes read as {label, value}[].
  }) as unknown as CustomFieldForm;
}

/** Whether the org settings say an unauthored DATE field starts at today. Absent settings ⇒ yes. */
function dateDefaultToToday(customField: CustomField, dateDefaults?: DateDefaultSettings): boolean {
  return customField.required
    ? (dateDefaults?.default_required_dates_to_today ?? true)
    : (dateDefaults?.default_optional_dates_to_today ?? true);
}

export function GenerateCustomFieldBuilderForm(
  fb: FormBuilder,
  values?: CustomField,
  reservedFieldNames: string[] = [],
  equationEligibleFields: string[] | null = []
): CustomFieldForm {
  const isNew = !values?.field;
  const isDocument = values?.type === CustomFieldType.FILE_UPLOAD;
  // FILE_UPLOAD "field" is an AttachmentType enum key chosen from a dropdown (UPPER_SNAKE_CASE),
  // not a hand-typed custom-field code, so the c_-prefix rule must not apply to it.
  const fieldPattern = isDocument ? ANY_SNAKE_CASE : isNew ? CUSTOM_FIELD_CODE : ANY_SNAKE_CASE;
  const fieldValidators = [Validators.required, Validators.pattern(fieldPattern)];
  if (reservedFieldNames.length) {
    fieldValidators.push(AvailableFieldValidator(reservedFieldNames));
  }

  const fieldControl = new FormControl(values?.field ?? undefined, fieldValidators);

  const optionsValidation =
    values?.type === CustomFieldType.SELECT ? [Validators.required, Validators.minLength(1)] : [];

  // The option-definition rows are only meaningful for MULTI_SELECT and RADIO; every other type keeps
  // label-equals-value (Req 2.6). The controls are ADDED ONLY for those types, not merely left
  // empty — the dialogs close with getRawValue() and the PM builder and the attorney intake
  // customizer write that over every field of every section, so an unconditional control would
  // start persisting `option_definitions: []` / `render_separator: null` onto every existing field
  // of every type, which Req 1.4 forbids.
  //
  // No validator is attached here. A blocking rule belongs only where the list can actually be
  // fixed: the intake-form-builder dialog shows these options read-only, disables CLOSE on invalid
  // and offers CANCEL only in add mode, so a validator there would trap an admin in an
  // undismissable dialog over a list they cannot edit. The editable editor component attaches it.
  const isMultiSelect = values?.type === CustomFieldType.MULTI_SELECT;
  // RADIO shares the {value, label} option rows but never the separator: a single-valued field has
  // nothing to join.
  const hasOptionRows = isMultiSelect || values?.type === CustomFieldType.RADIO;
  // With no definitions, each `options` entry is an option whose label equals its value (Req 2.2).
  // Without this a MULTI_SELECT derived from a legacy SurveyJS checkbox question would open with an
  // empty option list and no way to tell that from a genuinely new field.
  const multiSelectControls = hasOptionRows
    ? {
      option_definitions: fb.array(
        choiceSeedOptions(values).map(option => GenerateCustomFieldOptionForm(fb, option))
      ),
      // RADIO shares the option rows but never the separator — a single-valued field has nothing
      // to join, so the control must not exist on one.
      ...(isMultiSelect ? {render_separator: [values?.render_separator ?? RenderSeparator.COMMA]} : {})
    }
    : {};

  // `default_value` and `mask` are added conditionally for exactly the reason the option controls are:
  // both dialogs close with getRawValue() and write it over every field of every section, so an
  // unconditional control would start persisting `default_value: null` / `mask: null` onto every
  // existing field of every type. A type with no scalar value to default gets no control at all.
  const defaultValueControls = DEFAULTABLE_TYPES.has(values?.type)
    ? {default_value: [values?.default_value ?? null]}
    : {};
  // TEXT only — a mask on any other control could reshape a numeric, date or choice value.
  const maskControls = values?.type === CustomFieldType.TEXT ? {mask: [values?.mask ?? null]} : {};

  // convert the following to FormControl syntax. The cast reconciles the option-definition array,
  // which only this (field-definition) form carries — the render form has no options editor.
  const form = fb.group({
    value: [{ value: undefined, disabled: false }], // only here so custom field component doesn't blow up
    field: fieldControl,
    label: [values?.label ?? '', [Validators.required]],
    hint: values?.hint ?? undefined,
    hint_more_info: values?.hint_more_info ?? undefined,
    order: [values?.order ?? undefined],
    columns: [values?.columns ?? 4, [Validators.required]],
    type: [values?.type ?? undefined, [Validators.required]],
    link_id: values?.link_id ?? undefined,
    required: values?.required ?? false,
    options: [values?.options ?? [], optionsValidation],
    ...multiSelectControls,
    ...defaultValueControls,
    ...maskControls,
    false_label: [values?.false_label ?? undefined],
    true_label: [values?.true_label ?? undefined],
    include_in_total: [values?.include_in_total ?? (values?.type === CustomFieldType.RENT_HISTORY)],
    monetary: [values?.monetary ?? (values?.type === CustomFieldType.DECIMAL)],
    hide: [values?.hide ?? false],
    hide_from_intake: [values?.hide_from_intake ?? false],
    prevent_removal: [values?.prevent_removal ?? false],
    built_in_field: [values?.built_in_field || CUSTOM_FIELDS.some((f) => f.field === values?.field)],
    description: [values?.description ?? undefined],
    example: [values?.example ?? undefined]
  }) as CustomFieldForm;

  if (values?.type === CustomFieldType.CALCULATED) {
    form.get('hint')?.disable();
    form.get('hint_more_info')?.disable();
    form.get('value')?.enable();
    if (values?.value) {
      form.get('value').setValue(values.value);
    }
    // Calculated fields are computed, never collected on intake: always hidden there and
    // never required. The editor dialog hides both toggles for this type.
    form.get('hide_from_intake').setValue(true);
    form.get('required').setValue(false);
    // Only validate the equation in contexts that actually edit it (they pass the eligible
    // fields, e.g. the Field Settings editor). Callers like the intake form customizer pass
    // no/empty eligible fields and don't render the equation — attaching the validators there
    // makes the form group permanently invalid and soft-locks dialogs gated on validity.
    if (equationEligibleFields?.length) {
      form.get('value').addValidators([Validators.required, ValidateQuillEquation(equationEligibleFields)]);
    }
  }

  return form;
}
